<template>
    <ConfirmDialog header="Confirm delete"
                   question="Do you want to choose to delete selected file?"
                   v-model="isConfirmationOpen"
                   :operation="onFileRemove"
                   :parameter="fileSelectedToDelete" />

    <v-file-input chips
                  multiple
                  single-line
                  variant="solo"
                  class="no-prepend file-input"
                  show-size="1024"
                  :label="__('Select files')"
                  :disabled="disabled"
                  :style="style"
                  :class="{ 'dragover': isDragover }"
                  :clearable="false"
                  :error-messages="errorMessage"
                  @update:modelValue="onFileSelect($event)"
                  @drop.prevent="onFileSelect($event.dataTransfer.files)"
                  @dragover.prevent="isDragover = true"
                  @dragenter.prevent="isDragover = false"
                  @dragleave.prevent="isDragover = false"
                  :modelValue="attachments?.hasOwnProperty(category) ? attachments[category] : []">
        <template #selection="{ fileNames }">
            <v-chip class="file-chip"
                    :class="{ 'dragover': isDragover }"
                    v-for="(file, index) in (attachments?.hasOwnProperty(category) ? attachments[category] : [])"
                    variant="outlined"
                    :key="index">
                <span v-if="imgPosition == 'left' && file.type && file.type.startsWith('image/')"
                      style="margin-right:8px;">
                    <img :src="getImagePreview(file)"
                         alt="miniatura"
                         :style="`max-width: ${imgSize}px; max-height: ${imgSize}px; object-fit: cover; border-radius: 3px; margin-right: 8px;`" />
                </span>

                <span @click.stop="openConfirmationWindow(getFileNameWithSize(file))"
                      class="remove-icon">
                    <i class="fa-solid fa-trash-can"></i>
                </span>

                <span v-if="canCreateDownloadLink(getFileNameWithSize(file))"
                      @click.stop="downloadFile(getFileNameWithSize(file))"
                      class="download-link">
                    <span v-if="isShortNames">
                        {{ fileNames[index] }}
                    </span>
                    <span v-else>
                        {{ getFileNameWithSize(file) }}
                    </span>
                </span>

                <span v-else
                      class="just-added-file">
                    <span v-if="isShortNames">
                        {{ fileNames[index] }}
                    </span>
                    <span v-else>
                        {{ getFileNameWithSize(file) }}
                    </span>
                </span>

                <span v-if="imgPosition == 'right' && file.type && file.type.startsWith('image/')"
                      style="margin-right:8px;">
                    <img :src="getImagePreview(file)"
                         alt="miniatura"
                         :style="`max-width: ${imgSize}px; max-height: ${imgSize}px; object-fit: cover; border-radius: 3px; margin-left: 8px;`" />
                </span>
            </v-chip>
        </template>
        <template #message="error">
            <div>{{ error.message }}</div>
        </template>
    </v-file-input>
    <div :name="fieldName"
         style="margin-top:-20px"></div>
</template>

<script setup>
import { ref, watch } from 'vue'
import axios from 'axios'
import { VFileInput } from 'vuetify/components/VFileInput'
import { VChip } from 'vuetify/components/VChip'
import ConfirmDialog from '@pages/Crud/ConfirmDialog.vue'
import { getSessionValue } from '@pages/Helper/utils.js'
import { prepareFiles } from '@pages/Helper/crud_utils.js'
import { __ } from '@pages/Helper/utils.js'

const props = defineProps({
    files: Object,
    style: Object,
    category: String,
    isShortNames: {
        type: Boolean,
        default: false
    },
    allowedExtensions: Array, //['jpg', 'jpeg', 'png', 'gif'];
    disallowedExtensions: {
        type: Array,
        default: () => ['exe', 'bat', 'sh', 'php', 'html', 'js', 'css']
    },
    maxFiles: {
        type: [Number, null],
        default: null
    },
    maxFileSize: {
        type: [Number, null],
        default: null
    },
    maxTotalSize: {
        type: [Number, null],
        default: null
    },
    dataCrud: String,
    disabled: Boolean,
    fieldName: {
        type: String,
        default: null
    },
    imgPosition: {
        type: String,
        default: null
    },
    imgSize: {
        type: Number,
        default: 32
    }
})

const attachments = defineModel('files', { default: [] })
const metadata = ref([])
const errorMessage = ref('')
const isDragover = ref(false);
const isConfirmationOpen = ref(false)
const fileSelectedToDelete = ref(null)
const imagePreviews = ref({});

//-----------------------------------------------------------------------------------------------

watch(() => attachments.value, (newValue, oldValue) => {
    const files = prepareFiles(newValue)
    if (files.hasOwnProperty(props.category) && Array.isArray(files[props.category])) {
        attachments.value[props.category] = [...files[props.category].map(elem => elem.file)]
        metadata.value[props.category] = [...files[props.category].map(elem => elem.metadata)]
    }
}, { deep: true, immediate: true })

//-----------------------------------------------------------------------------------------------

const getFileNameWithSize = (file) => {
    return file.name + ' (' + (file.size / 1024).toFixed(1) + ' kB)'
}

//-----------------------------------------------------------------------------------------------

const onFileSelect = (files) => {
    if (files instanceof FileList) {
        files = Array.from(files);
    }

    // Inicjalizuj attachments jeśli jest null
    if (!attachments.value || typeof attachments.value !== 'object') {
        attachments.value = {};
    }

    // Inicjalizuj kategorię jeśli nie istnieje
    if (!attachments.value[props.category]) {
        attachments.value[props.category] = [];
    }

    if (validateFile(files)) {
        attachments.value[props.category] = [...attachments.value[props.category] ?? [], ...files]
    }
    isDragover.value = false
}

//-----------------------------------------------------------------------------------------------

const onFileRemove = (fileNameWithSize) => {
    const fileToRemove = fileNameWithoutSize(fileNameWithSize)
    attachments.value[props.category] = attachments.value[props.category].filter(file => {
        return file.name !== fileToRemove
    });
    errorMessage.value = null
}

//-----------------------------------------------------------------------------------------------

const validateFile = (newFiles) => {
    if (!newFiles || !Array.isArray(newFiles) || newFiles.length === 0) return true;

    const category = props.category
    // Zabezpieczenie przed null/undefined
    if (!attachments.value || typeof attachments.value !== 'object') {
        attachments.value = {};
    }
    if (!attachments.value[category]) {
        attachments.value[category] = [];
    }

    const currentFiles = attachments?.value[category] ?? [];
    const totalFiles = (currentFiles?.length ?? 0) + newFiles.length;

    if (props.maxFiles && props.maxFiles > 0 && totalFiles > props.maxFiles) {
        errorMessage.value = __('Maximum number of files has been exceeded') + ': ' + props.maxFiles;
        return false;
    }

    // Walidacja rozmiaru pojedynczych plików
    if (props.maxFileSize) {
        const oversizedFiles = newFiles.filter(file => file.size > props.maxFileSize);
        if (oversizedFiles.length > 0) {
            const maxSizeMB = (props.maxFileSize / (1024 * 1024)).toFixed(1);
            const oversizedFileNames = oversizedFiles.map(f => f.name).join(', ');
            errorMessage.value = __('Files exceed maximum size') + ': ' + maxSizeMB + 'MB: ' + oversizedFileNames;
            return false;
        }
    }

    // Walidacja całkowitego rozmiaru wszystkich plików
    if (props.maxTotalSize) {
        const currentFilesSize = currentFiles.reduce((total, file) => total + (file.size || 0), 0);
        const newFilesSize = newFiles.reduce((total, file) => total + file.size, 0);
        const totalSize = currentFilesSize + newFilesSize;

        if (totalSize > props.maxTotalSize) {
            const maxTotalSizeMB = (props.maxTotalSize / (1024 * 1024)).toFixed(1);
            const currentSizeMB = (currentFilesSize / (1024 * 1024)).toFixed(1);
            const newSizeMB = (newFilesSize / (1024 * 1024)).toFixed(1);
            errorMessage.value = __('Total file size exceeds limit') + ': ' + maxTotalSizeMB + 'MB. ' + __('Current') + ': ' + currentSizeMB + 'MB, ' + __('adding') + ': ' + newSizeMB + 'MB';
            return false;
        }
    }

    if (props.allowedExtensions) {
        const invalidFiles = newFiles.filter(file => {
            const fileExtension = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase();
            return !props.allowedExtensions.includes(fileExtension);
        });

        if (invalidFiles.length > 0) {
            errorMessage.value = __('Allowed file types are') + ': ' + props.allowedExtensions.join(', ');
            return false;
        }
    }

    if (props.disallowedExtensions) {
        const invalidFiles = newFiles.filter(file => {
            const fileExtension = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase();
            return props.disallowedExtensions.includes(fileExtension);
        });

        if (invalidFiles.length > 0) {
            errorMessage.value = __('Attempting to upload files of unacceptable types');
            return false;
        }
    }

    errorMessage.value = null
    return true;
};

//-----------------------------------------------------------------------------------------------

const downloadFile = async (fileNameWithSize) => {
    try {
        const fileName = fileNameWithoutSize(fileNameWithSize)
        const fileMetadata = metadata.value[props.category].find(elem => elem.fileName === fileName)
        const filePath = fileMetadata?.filePath

        if (!filePath) {
            console.error(__('File not found'));
            return
        }

        const path = getSessionValue('environment.storagePath') ? getSessionValue('environment.storagePath') + filePath : filePath
        const response = await axios.get(path, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');

        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
    } catch (error) {
        console.error(__('Error downloading file') + ': ', error);
        //wyświetl komunikat o błędzie użytkownikowi:
        errorMessage.value = __('Error downloading file') + ': ' + error.response.status + ' ' + error.response.statusText;
    }
};

//-----------------------------------------------------------------------------------------------

const fileNameWithoutSize = (fileNameWithSize) => {
    return fileNameWithSize.slice(0, fileNameWithSize.lastIndexOf('(')).trim()
}

//-----------------------------------------------------------------------------------------------

const canCreateDownloadLink = (fileNameWithSize) => {
    const fileName = fileNameWithoutSize(fileNameWithSize)
    return (metadata.value[props.category] ?? []).some(elem => elem.fileName === fileName)
}

//-----------------------------------------------------------------------------------------------

const openConfirmationWindow = (fileName) => {
    fileSelectedToDelete.value = fileName
    isConfirmationOpen.value = true
}

//-----------------------------------------------------------------------------------------------

const getImagePreview = (file) => {
    if (imagePreviews.value[file.name]) {
        return imagePreviews.value[file.name];
    }
    if (file && file.type && file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
            imagePreviews.value[file.name] = e.target.result;
        };
        reader.readAsDataURL(file);
    }
    return '';
};

//-----------------------------------------------------------------------------------------------

</script>

<style lang="scss" scoped>
.remove-icon {
    cursor: pointer;
    color: red;
    margin-right: 5px;
}

.download-link {
    cursor: pointer;
    color: blue;
}

.just-added-file {
    color: black;
}

:deep(.v-input__prepend) {
    display: none;
}

.file-input {
    margin-top: 2px !important;
    border: 0;
}

:deep(.v-field__input .file-chip) {
    border-radius: 5px;
    border: 1px solid #000;
    box-shadow: 5px 5px 10px rgba(0, 0, 0, 0.5);
    margin: 0 5px;
    position: relative;
    z-index: 1
}


:deep(.v-field__field input[type="file"]) {
    position: absolute !important;
    width: 100%;
    height: 100%;
    opacity: 0;
    z-index: 0
}

:deep(.v-field__field) {
    display: flex;
}

.file-input.dragover {
    background-color: lightcyan !important;
    border-radius: 5px;
    border: 1px dotted lightgreen;
}

.file-chip.dragover {
    background-color: lightblue !important;
}

:deep(.v-input__details) {

    border: 0;
}

.file-chip {
    min-height: 40px;
    align-items: center;
    display: flex;
}
</style>
